import React, { useState, useEffect } from 'react';
import { Plus, ChevronDown, Clock, Trash2 } from 'lucide-react';
import { getFlatRateItems, FlatRateItem as ImportedFlatRateItem } from '../../lib/flat-rates';
import { useBusiness } from '../../contexts/BusinessContext';
import SelectFlatRateModal from '../SelectFlatRateModal';
import { supabase } from '../../lib/supabase';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { FlatRateTemplate, getFlatRateTemplatesByService } from '../../lib/flatRateTemplates';
import { selectInputContentOnFocus } from '../../lib/utils';

type BookingFlatRatesTabProps = {
  bookingId: string;
  businessId: string;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  booking: { 
    status: string;
    services?: Array<{
      service_id: string;
      name: string;
    }>;
  };
};

interface ExtendedFlatRateItem extends ImportedFlatRateItem {
  resource_id: string | null;
  resource_flat_rate_amount: number | null;
  resource_hourly_rate: number | null;
  resource_first_name?: string | null;
  resource_last_name?: string | null;
  custom_hours?: number;
}

interface BookingFlatRateEntry {
  id: string;
  name: string;
  description: string | null;
  hours: number;
  resource_id: string | null;
  resource_flat_rate_amount: number | null;
  resource_hourly_rate: number | null;
  resource_first_name?: string | null;
  resource_last_name?: string | null;
  custom_hours?: number;
}

interface ResourceGroup {
  id: string | null;
  name: string;
  items: ExtendedFlatRateItem[];
  totalHours: number;
  totalEarnings: number;
}

function BookingFlatRatesTab({
  bookingId,
  businessId,
  onUpdate,
  onError,
  booking
}: BookingFlatRatesTabProps) {
  const { selectedBusiness } = useBusiness();
  const [resourceGroups, setResourceGroups] = useState<ResourceGroup[]>([]);
  const [isExpanded, setIsExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSelectFlatRateModalOpen, setIsSelectFlatRateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hoursLoading, setHoursLoading] = useState<Set<string>>(new Set());
  const [templates, setTemplates] = useState<FlatRateTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const toggleGroup = (groupId: string) => {
    setIsExpanded(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const { data: flatRateData, error } = await supabase
        .from('booking_flat_rates_with_details')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group entries by resource
      const entriesByResource = (flatRateData || []).reduce<Record<string, ExtendedFlatRateItem[]>>((acc, entry) => {
        const resourceId = entry.resource_id || 'unassigned';
        if (!acc[resourceId]) {
          acc[resourceId] = [];
        }
        acc[resourceId].push({
          id: entry.id,
          business_id: entry.business_id,
          name: entry.item_name,
          description: entry.item_description,
          category_id: entry.category_id,
          hours: entry.hours,
          order_index: entry.order_index,
          created_at: entry.created_at,
          updated_at: entry.updated_at,
          resource_id: entry.resource_id,
          resource_flat_rate_amount: entry.resource_flat_rate_amount,
          resource_hourly_rate: entry.resource_hourly_rate,
          resource_first_name: entry.resource_first_name,
          resource_last_name: entry.resource_last_name,
          custom_hours: entry.custom_hours
        });
        return acc;
      }, {});

      // Calculate totals and create resource groups
      const groups = Object.entries(entriesByResource).map(([resourceId, items]) => {
        const totalHours = items.reduce((sum, item) => sum + (item.custom_hours ?? item.hours), 0);
        const totalEarnings = items.reduce((sum, item) => {
          const rate = item.resource_flat_rate_amount || item.resource_hourly_rate || 0;
          return sum + (rate * (item.custom_hours ?? item.hours));
        }, 0);

        return {
          id: resourceId === 'unassigned' ? null : resourceId,
          name: resourceId === 'unassigned' ? 'Unassigned' : 
            `${items[0].resource_first_name} ${items[0].resource_last_name}`,
          items,
          totalHours,
          totalEarnings
        };
      });

      setResourceGroups(groups);
      // Auto-expand if only one group
      if (groups.length === 1) {
        setIsExpanded(new Set([groups[0].id || 'unassigned']));
      }
    } catch (err) {
      console.error('Error loading flat rate entries:', err);
      onError(err instanceof Error ? err.message : 'Failed to load flat rate entries');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (booking.status === 'completed') {
      onError('Cannot delete flat rate items from completed bookings');
      return;
    }
    setItemToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    try {
      // First get the booking status
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select('status')
        .eq('id', bookingId)
        .single();

      if (bookingError) throw bookingError;

      // Delete the flat rate entry
      const { error } = await supabase
        .from('booking_flat_rates')
        .delete()
        .eq('id', itemToDelete);

      if (error) throw error;

      // If booking is completed, manually trigger the handle_flat_rate_changes function
      if (bookingData?.status === 'completed') {
        // Instead of calling the function directly, we'll update the entry to trigger the function
        const { error: updateError } = await supabase
          .from('booking_flat_rates')
          .update({ hours: 0 }) // This will trigger the handle_flat_rate_changes function
          .eq('id', itemToDelete);

        if (updateError) throw updateError;
      }

      // Refresh data
      await onUpdate();
      await loadEntries();
    } catch (err) {
      console.error('Error deleting flat rate:', err);
      onError(err instanceof Error ? err.message : 'Failed to delete flat rate');
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAddMultipleItems = async (
    item: ImportedFlatRateItem,
    hours: number,
    resourceId: string
  ) => {
    try {
      const actualResourceId = resourceId === 'unassigned' ? null : resourceId;

      // Insert the new flat rate entry
      const { data, error } = await supabase
        .from('booking_flat_rates')
        .insert([{
          booking_id: bookingId,
          item_id: item.id,
          resource_id: actualResourceId,
          hours: hours, // Set the hours column
          custom_hours: hours // Set custom_hours to the provided hours
        }])
        .select();

      if (error) {
        console.error('Error inserting flat rate:', error);
        throw new Error(`Failed to add flat rate: ${error.message}`);
      }

      // Refresh data
      await onUpdate();
      await loadEntries();
      setIsSelectFlatRateModalOpen(false);
    } catch (err) {
      console.error('Error adding flat rates:', err);
      onError(err instanceof Error ? err.message : 'Failed to add flat rates');
    }
  };

  const handleAddClick = () => {
    if (booking.status === 'completed') {
      onError('Cannot add flat rate items to completed bookings');
      return;
    }
    setIsSelectFlatRateModalOpen(true);
  };

  const loadTemplates = async () => {
    if (!booking.services?.length) {
      setTemplates([]);
      return;
    }

    setTemplatesLoading(true);
    try {
      const allTemplates: FlatRateTemplate[] = [];
      
      // Fetch templates for each service
      for (const service of booking.services) {
        const serviceTemplates = await getFlatRateTemplatesByService(service.service_id);
        allTemplates.push(...serviceTemplates);
      }

      // Remove duplicates if a template is used in multiple services
      const uniqueTemplates = allTemplates.filter((template, index, self) =>
        index === self.findIndex((t) => t.id === template.id)
      );

      setTemplates(uniqueTemplates);
    } catch (err) {
      console.error('Error loading templates:', err);
      onError(err instanceof Error ? err.message : 'Failed to load flat rate templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    if (bookingId) {
      loadEntries();
    }
  }, [bookingId, businessId]);

  useEffect(() => {
    if (booking.services?.length) {
      loadTemplates();
    }
  }, [booking.services]);

  return (
    <>
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden min-h-[400px] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <h3 className="text-sm font-medium text-gray-900">Flat Rate Items</h3>
          <button
            onClick={handleAddClick}
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Item
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading entries...</p>
              </div>
            </div>
          ) : resourceGroups.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-sm text-gray-500">
                No flat rate entries yet
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {resourceGroups.map((group) => (
                <div key={group.id || 'unassigned'} className="border-b border-gray-200">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleGroup(group.id || 'unassigned')}
                  >
                    <div className="flex items-center space-x-3">
                      <ChevronDown 
                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                          isExpanded.has(group.id || 'unassigned') ? 'rotate-180' : ''
                        }`}
                      />
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{group.name}</h3>
                        <div className="text-xs text-gray-500">
                          {group.items.length} items • {group.totalHours} hours
                        </div>
                      </div>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      ${group.totalEarnings.toFixed(2)}
                    </div>
                  </div>
                  
                  {isExpanded.has(group.id || 'unassigned') && (
                    <div className="bg-gray-50 divide-y divide-gray-200">
                      {group.items.map((item) => (
                        <div key={item.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">{item.name}</h4>
                              {item.description && (
                                <p className="mt-1 text-sm text-gray-500">{item.description}</p>
                              )}
                              <div className="mt-2 flex items-center text-xs text-gray-500">
                                <Clock className="h-3.5 w-3.5 mr-1" />
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0.1"
                                  value={item.custom_hours ?? item.hours}
                                  disabled={true}
                                  onFocus={selectInputContentOnFocus}
                                  className={`w-20 h-6 px-1 text-xs border border-gray-300 rounded bg-gray-50 text-right cursor-not-allowed`}
                                />
                                <span className="ml-1">hours</span>
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                              ${((item.resource_flat_rate_amount || item.resource_hourly_rate || 0) * item.hours).toFixed(2)}
                              <button
                                onClick={() => handleDelete(item.id)}
                                disabled={booking.status === 'completed'}
                                className={`ml-2 p-1 rounded-md ${
                                  booking.status === 'completed' 
                                    ? 'text-gray-300 cursor-not-allowed' 
                                    : 'text-red-400 hover:text-red-600 hover:bg-red-50'
                                }`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden min-h-[200px] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <h3 className="text-sm font-medium text-gray-900">Flat Rate Estimated Hours</h3>
        </div>

        <div className="flex-1 overflow-y-auto">
          {templatesLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading templates...</p>
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-center text-sm text-gray-500">
                No flat rate templates found for the selected services
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {templates.map((template) => (
                <div key={template.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{template.title}</h4>
                      {template.description && (
                        <p className="mt-1 text-sm text-gray-500">{template.description}</p>
                      )}
                      <div className="mt-2 flex items-center text-xs text-gray-500">
                        <Clock className="h-3.5 w-3.5 mr-1" />
                        <span>{template.hours} hours</span>
                        {template.rate_per_hour && (
                          <span className="ml-2">• ${template.rate_per_hour}/hr</span>
                        )}
                      </div>
                    </div>
                    {template.rate_per_hour && (
                      <div className="text-sm font-medium text-gray-900">
                        ${(template.hours * template.rate_per_hour).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <SelectFlatRateModal
        isOpen={isSelectFlatRateModalOpen}
        onClose={() => setIsSelectFlatRateModalOpen(false)}
        bookingId={bookingId}
        onAdd={handleAddMultipleItems}
        selectedResourceId={null}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Flat Rate"
        message="Are you sure you want to delete this flat rate entry? This action cannot be undone."
        loading={deleteLoading}
      />
    </div>
    </>
  );
}

export default BookingFlatRatesTab;